<template>
  <div class="shop-cart is-max">
    <div class="shop-cart__title">
      {{ data.title }}
    </div>

    <ShopCartDiscount
      v-if="!hasDiscount && data.show_discount"
    />

    <ShopCartList
      :data="cart"
      is-editable="true"
    />

    <transition name="fade-in">
      <ShopCartShipping
        v-if="checkoutStore.getShippingMethod?.isClickAndCollect === false
          && data.show_shipping_costs"
      />
    </transition>

    <div v-if="isClickAndCollect" class="shop-cart__click-and-collect-text">
      {{ useTranslation('general', 'cart_click_and_collect_notice') }}
    </div>

    <div
      v-if="!cartStore.isEmpty"
      class="shop-order__summary"
    >
      <div class="shop-order__summary-text">
        {{ useTranslation('general', 'total_inclusive_mwst') }}
      </div>

      <div class="shop-order__summary-amount">
        {{ currency }} {{ totalAmount.toFixed(2) }}
      </div>
    </div>

    <div v-if="!data.is_locked" class="shop-cart__buttons">
      <div class="shop-cart__controls">
        <AtomButton
          appereance="inverted"
          :text="useTranslation('general', 'back_to_shop')"
          @click="navigateToShop()"
          @keypress="navigateToShop()"
        />

        <AtomButton
          v-if="!cartStore.isEmpty"
          appereance="inverted"
          :text="useTranslation('general', 'cart_delete')"
          @click="cartStore.clearCart"
          @keypress="cartStore.clearCart"
        />
      </div>

      <div v-if="cartStore.getHasProducts" class="shop-cart__controls">
        <AtomButton
          v-for="link in data.navigation_links"
          :key="link._uid"
          :text="link.text"
          button-type="dummy"
          @click="navigateToCheckout(link)"
        />
      </div>
    </div>

    <FormConfirmation
      v-if="data.confirmation_popup?.length === 1"
      :data="data.confirmation_popup[0]"
      :is-open="isConfirmationOpen"
    />
  </div>
</template>

<script setup>
const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },

    isEditable: {
        type: Boolean,
        default: false,
    },
});

provide('show-prev', false);
provide('show-next', true);

const isEditable = computed(() => !props?.data?.is_locked || props.isEditable);

provide('is-editable', isEditable.value);

const cartStore = useCartStore();
const checkoutStore = useCheckoutStore();

const cart = computed(() => cartStore.getCart);
const currency = computed(() => cartStore.getCurrency);
const totalAmount = computed(() => cartStore.getTotalAmount + checkoutStore.getShippingCosts);
const isClickAndCollect = computed(() => cartStore.isClickAndCollect);
const hasDiscount = computed(() => cartStore.getHasDiscount);

const router = useRouter();
const navigateToShop = () => {
    router.push('/');
};

const { sendEvent } = useGTM();
const route = useRoute();

const sendViewCartEvent = () => {
    if (cart.value.length > 0) {
        sendEvent({
            event: 'view_cart',
            ecommerce: {
                currency: currency.value,
                value: totalAmount.value,
                items: cart.value.map((item) => ({
                    id: item?.product?.id,
                    name: item?.product?.name,
                    pharma_code: item?.product?.pharma_code,
                    price: item?.product?.price,
                    is_sendable: item?.product?.is_sendable || false,
                    needs_prescription: item?.product?.needs_prescription || false,
                    short_text: item?.product?.short_text,
                    description: item?.product?.description,
                })),
            },
        });
    }
};

const alertBoxStore = useAlertBoxStore();
onMounted(() => {
    if (!isEditable.value) return;

    // Get query from the url
    const { query } = route;

    if (query.aborted === 'failed') {
        // Show notification popup
        alertBoxStore.setAlertBoxData('error', {
            error_text: useTranslation('general', 'abort_failed'),
        });
        alertBoxStore.toggleAlertbox();
    }
    if (query.aborted === 'canceled') {
        // Show notification popup
        alertBoxStore.setAlertBoxData('error', {
            error_text: useTranslation('general', 'abort_canceled'),
        });
        alertBoxStore.toggleAlertbox();
    }

    sendViewCartEvent();
});

const { alertConfirmation, isConfirmationOpen } = useFormConfirmation();
const navigateToCheckout = async (link) => {
    const url = link.link?.cached_url;

    if (isClickAndCollect.value) {
        const result = await alertConfirmation();
        if (result) {
            navigateTo(`../${url}`);
        }
    } else {
        navigateTo(`../${url}`);
    }
};

watch(() => cart.value, () => {
    sendViewCartEvent();
}, { deep: true });
</script>

<style lang="scss">
.shop-cart {
    @include default-content-columns;
    @include fluid('margin-bottom', 60px, 120px);

    & + .block-cta-group {
        margin-top: -60px;
    }
}

.shop-cart__title {
    @include typo-font('regular');

    margin-bottom: 10px;
    color: $C_GREEN_DARK;
    font-size: 26px;
}
.shop-cart__controls {
    display: flex;
    justify-content: space-between;
}

.shop-order__summary{
    @include typo-font('regular');
    @include typo-size('label');
    @include fluid('margin-bottom', 20px, 50px);

    display: flex;
    justify-content: space-between;
    padding: 20px 0;
    color: $C_GREEN_DARK;
}
.shop-cart__click-and-collect-text {
    @include typo-font('regular');
    @include typo-size('nav');
    @include fluid('margin-bottom', 33px, 45px);
}

.shop-cart__buttons, .shop-cart__controls {
    display: flex;
    flex-wrap: wrap;
    column-gap: 9px;
    row-gap: 9px;
}

.shop-cart__buttons {
    display: flex;
    justify-content: space-between;
    row-gap: 35px;
}

.shop-cart__next,{
    margin-left: auto;

    @include mobile {
        margin-left: unset;
    }
}
</style>
